// React hook for managing push notifications
import { useState, useEffect } from 'react';
import pushNotificationService from '../services/pushNotifications';

interface PushNotificationState {
  isSupported: boolean;
  isSubscribed: boolean;
  permission: NotificationPermission;
  isLoading: boolean;
  error: string | null;
  backendSubscribed: boolean;
}

interface PushNotificationActions {
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  checkStatus: () => Promise<void>;
  sendTestNotification: () => Promise<void>;
}

export const usePushNotifications = () => {
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    isSubscribed: false,
    permission: 'default',
    isLoading: true,
    error: null,
    backendSubscribed: false,
  });

  // Check initial status
  useEffect(() => {
    checkStatus();
  }, []);

  // The browser can rotate a push subscription on its own (key rotation, or a
  // long-idle install). Without hearing about that, the hook keeps reporting a
  // subscription that no longer receives anything.
  //
  // `pushsubscriptionchange` fires in the service worker's own global scope,
  // not here, so the page cannot listen for it directly -- the worker has to
  // re-subscribe and post the result back. Note that public/sw.js does not
  // send that message yet, so this listener is inert until it does.
  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw?.addEventListener) return;

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'push-subscription-changed') checkStatus();
    };

    sw.addEventListener('message', onMessage);
    return () => {
      sw.removeEventListener('message', onMessage);
    };
  }, []);

  const checkStatus = async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const isSupported = pushNotificationService.isSupported();
      const status = await pushNotificationService.getSubscriptionStatus();

      setState({
        isSupported,
        isSubscribed: status.isSubscribed,
        permission: status.permission,
        backendSubscribed: status.backendSubscribed,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('[usePushNotifications] Error checking status:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }));
    }
  };

  const subscribe = async (): Promise<boolean> => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const success = await pushNotificationService.subscribe();

      if (success) {
        await checkStatus(); // Refresh status
      }

      setState((prev) => ({ ...prev, isLoading: false }));
      return success;
    } catch (error) {
      console.error('[usePushNotifications] Error subscribing:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to subscribe',
      }));
      return false;
    }
  };

  const unsubscribe = async (): Promise<boolean> => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const success = await pushNotificationService.unsubscribe();

      if (success) {
        await checkStatus(); // Refresh status
      }

      setState((prev) => ({ ...prev, isLoading: false }));
      return success;
    } catch (error) {
      console.error('[usePushNotifications] Error unsubscribing:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to unsubscribe',
      }));
      return false;
    }
  };

  const sendTestNotification = async (): Promise<void> => {
    try {
      await pushNotificationService.sendTestNotification();
    } catch (error) {
      console.error(
        '[usePushNotifications] Error sending test notification:',
        error
      );
      // Surface the failure the same way subscribe/unsubscribe do. Logging
      // alone left the driver tapping "send test" with no indication that it
      // had failed.
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to send test notification',
      }));
    }
  };

  const actions: PushNotificationActions = {
    subscribe,
    unsubscribe,
    checkStatus,
    sendTestNotification,
  };

  return {
    ...state,
    actions,
  };
};

export default usePushNotifications;
